import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { UnitLocaleEnum } from '../../../models/enums/results/result-units-locale.enum';
import { SensitivityItem } from '../../../models/results/sensitivity/sensitivity-item.model';
import Utils from '../../../util';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import ResultsUtils from '../../results-util';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { MatDialog } from '@angular/material/dialog';
import { SensitivityAIPopupComponent } from '../sensitivity-ai-popup/sensitivity-ai-popup.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SensitivityResultsItem } from '../../../models/results/sensitivity/sensitivity-results-item.model';
import { LooseObject } from '../../../models/loose-object.model';
import { PlotlyTrace } from '../../../models/results/plotly.model';
import { SensitivityResult, SensitivityResultInterface } from '../../../models/results/sensitivity/sensitivity-result.model';

@Component({
  selector: 'app-sensitivity-result-graph',
  templateUrl: './sensitivity-result-graph.component.html',
  styleUrls: ['./sensitivity-result-graph.component.scss']
})
export class SensitivityResultGraphComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  readonly SENSITIVITY_RESULT_VERSION = 1;
  /**
   * Current graph data.
   */
  curGraphData: PlotlyTrace[] = [];

  /**
   * Determines if the graph is being shown.
   */
  showChart = false;
  /**
   * Trigger to clean up subscriptions when the component is destroyed
   */
  onDestroyNotifier = new EventEmitter();
  /**
   * The list of inputs that can be selected for the sensitivity analysis.
   */
  inputs?: SensitivityItem[] = [];

  /**
   * An array that contains the selected media types.
   */
  selectedParameters: string[] = [];



  // select related properties

  /**
   * The list of parameters that can be selected by the user.
   */
  parameters: string[] = [];
  /**
   * An array that contains the available media type; used to populate the media type dropdown.
   */
  resultTypes: string[] = [];
  /**
   * The selected result type for the graph data.
   */
  selectedResult?: string;
  xvals: number[] = [];

  vals = [];

  /**
   * The label that will be shown on the y axis of the graph
   */
  yAxisLabel: string = '';
  /**
   * The selected unit type, not applicable for all results sets.
   */
  selectedUnitsType = UnitLocaleEnum.METRIC;


  /**
   * An array that contains the available options for the unit type (locale); used to populate
   * the unit type dropdown.
   */
  unitsTypes: DropdownDisplayItem[] = [
    new DropdownDisplayItem('RESULTS.GENERAL.METRIC', UnitLocaleEnum.METRIC),
    new DropdownDisplayItem('RESULTS.GENERAL.IMPERIAL', UnitLocaleEnum.IMPERIAL)
  ];
  /**
   * List of currency units that can be used to manipulate some result sets.
   */
  currencyUnits: Unit[] = [];
  /**
   * The selected currency unit.
   */
  selectedCurrencyUnit?: Unit;



  /**
   * Determines if the table is being shown.
   */
  showTable = false;
   /**
   * Determines if the pop up is being shown.
   */
  showCalcPopUp = false;
  // Table related properties

  /**
   * An array of the table headers for the feedstock breakdown table. These need to match the properties of the
   * FeedstockBreakdownDataItem class.
   */
  variableHeader = 'SENSITIVITY.RESULTS.VARIABLE_HEADER';
  baseValueHeader = 'SENSITIVITY.RESULTS.BASELINE_VALUE_HEADER';
  minChangeHeader = 'SENSITIVITY.RESULTS.MINIMUM_CHANGE_HEADER';
  minValueHeader = 'SENSITIVITY.RESULTS.MINIMUM_VALUE_HEADER';
  maxChangeHeader = 'SENSITIVITY.RESULTS.MAXIMUM_CHANGE_HEADER';
  maxValueHeader = 'SENSITIVITY.RESULTS.MAXIMUM_VALUE_HEADER';
  tableHeaders = [this.variableHeader, this.baseValueHeader, this.minChangeHeader, this.minValueHeader,
    this.maxChangeHeader, this.maxValueHeader];

  /**
   * Object of data items for the table.
   */
  tableData: Record<string, SensitivityResultsItem> = {};

  graphLayout: Partial<Plotly.Layout> = {};
  yMax = 0;
  yMin = 0;
  xMax = 0;
  xMin = 0;
  slope = 0.0;
  gradient = 0.0;
  /**
   * Determines if project service is ready for use.
   */
  projectServiceReady = false;
  /**
   * Determines if the unit service is ready for use.
   */
  unitServiceReady = false;
  /**
   * Determines if the results are ready for use.
   */
  resultsLoaded = false;
  /**
   * Determines if the currency units are ready for use.
   */
  currencyUnitsLoaded = false;
  /**
   * The sensitivity results object that will be used and processed to create the graph and table data the user
   * selects.
   * @private
   */
  private sensResultData: any;
  exchangeRateDate?: string;
  trigger?: string;
  inputTranslations: LooseObject = {};
  /**
   * Currency the sensitivity results were calculated with. In case a single currency was used and the base is not CAD.
   */
  resultsBaseCurrencyId?: string;


  /**
   * Constructor.
   * @param projectCommService provides functionality for loading data from the project.
   * @param unitService provided unit functionality.
   * @param toastrService provides toast popup functionality.
   * @param translationService
   */
  constructor(private projectCommService: ProjectCommunicationService,
              private unitService: UnitService,
              private toastrService: ConfiguredToastrService,
              private translationService: AnessaTranslationService,
              private dialog: MatDialog
  ) {
    super();
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Loads data and results.
   */
  ngOnInit(): void {
    this.selectedUnitsType = this.projectCommService.getResultProjectState()?.projectInfo?.defaults?.unitSystem === 'imperial' ? UnitLocaleEnum.IMPERIAL : UnitLocaleEnum.METRIC;
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.resultsLoaded = loaded;
        if (loaded) {
          this.setup();
        }
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitServiceReady = loaded;
        if (loaded) {
          this.setup();
        }
      });
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((data) => {
        this.projectServiceReady = data;
        if (data) {
          this.setup();
        }
      });
    this.projectCommService.projectReloadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.setup();
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.processInputTranslations();
        this.optionsChanged();
        this.trigger = language;
      });
  }

  /**
   * Process that result data and populates the table data.
   */
  processTableData() {
    this.tableData = {};
    const tempData: Record<string, SensitivityResultsItem> = {};
    this.showTable = false;
    if (this.inputs && this.inputs.length > 0) {
      for (const input of this.inputs) {
        const precision = this.projectCommService.getPrecision();
        const item: SensitivityResultsItem = new SensitivityResultsItem();
        const unitDisplay = Unit.parseUnitDisplayName(this.translationService.get(input.unit));
        item.variableHeader = this.inputTranslations[input.id] + ' (' + unitDisplay + ')';
        if (input.value && (input.min || input.min === 0) && input.max) {
          item.baseline = Utils.roundToPrecisionFixed(input.value, precision);
          item.minChange = Utils.roundToPrecisionFixed((Math.abs(input.min / input.value - 1)) * 100, precision) + '%';
          item.minValue = Utils.roundToPrecisionFixed(input.min, precision);
          item.maxChange = Utils.roundToPrecisionFixed(((input.max / input.value) - 1) * 100, precision) + '%';
          item.maxValue = Utils.roundToPrecisionFixed(input.max, precision);
          tempData[this.inputTranslations[input.id] + ' (' + input.unit + ')'] = Utils.deepCopy(item);
        }
      }
      this.tableData = { ...tempData };
      this.showTable = true;
    }
  }

  /**
   * Gets called whenever an option (dropdown) is changed and checks if it's possible to process data for the
   * graph and table.
   */
  optionsChanged(): void {
    if (
      this.selectedParameters
      && this.selectedParameters.length > 0
      && this.selectedResult
      && this.selectedCurrencyUnit
    ) {
      this.processGraphData();
      this.processTableData();
    }
  }

  /**
   * Determines if the unit type dropdown should be shown.
   */
  showUnitTypeDropdown(): boolean {
    return this.selectedResult === ResultSetNames.RNG || this.selectedResult === ResultSetNames.BIOGAS;
  }

  /**
   * Determines if the currency type dropdown should be shown.
   */
  showCurrencyUnitsDropdown(): boolean {
    return this.selectedResult === ResultSetNames.NPV;
  }

  /**
   * Sets up data options for the user.
   * @private
   */
  private setup() {
    if (this.unitServiceReady && this.projectServiceReady && this.resultsLoaded) {
      const sensResultsEvent = this.projectCommService.fetchSensResults();
      if (sensResultsEvent) {
        sensResultsEvent
          .pipe(take(1))
          .subscribe(res => {
            if (res) {
              const sensResData = res.obj;
              if (!sensResData) {
                this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.NO_DATA'));
                return;
              }
              let version = sensResData['SENSITIVITY~VERSION'];
              delete sensResData['SENSITIVITY~VERSION'];
              if (!version && version !== this.SENSITIVITY_RESULT_VERSION) {
                this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.UPDATE'));
              }

              if ('SENSITIVITY~RESULT~CURRENCY' in sensResData) {
                this.resultsBaseCurrencyId = sensResData['SENSITIVITY~RESULT~CURRENCY'];
                delete sensResData['SENSITIVITY~RESULT~CURRENCY'];
              }
              /**
               * Exchange rate retrieval flow:
               * 1. from generated sensitivity data
               * 2. from currently loaded baseline results data
               * 3. latest exchange rates available
               */
              let exchangeRateId: string;
              if ('RESULTS~DATA~CREATED' in sensResData) {
                exchangeRateId = sensResData['RESULTS~DATA~CREATED'];
                delete sensResData['RESULTS~DATA~CREATED'];
              } else {
                const results = this.projectCommService.getResults();
                if (results && results.created) {
                  exchangeRateId = results.created;
                }
              }
              this.sensResultData = Utils.ObjectMapper(sensResData, (val: SensitivityResultInterface) => { return new SensitivityResult(val) });
              this.inputs = this.projectCommService.getSensitivityInputs();
              this.processInputTranslations();
              this.populateParameters();
              this.populateResultSets();
              this.populateCurrencyUnits(exchangeRateId);
            } else {
              throw Error('failed to retrieve sensitivity results');
            }
          });
      }
    }
  }

  /**
   * Populates the parameter dropdown data.
   * @private
   */
  private populateParameters() {
    if (this.sensResultData) {
      this.parameters = Object.keys(this.sensResultData);
      this.selectedParameters = this.parameters;
    }
  }
 /**
   * Populates the pop up for back calculating the values.
   * @private
   */
  public showPopUp(){
    let baseCurrency = this.resultsBaseCurrencyId;
    if (!baseCurrency) {
      baseCurrency = this.projectCommService.getBaseCurrencyId();
    }
    this.dialog.open(SensitivityAIPopupComponent, {
      width: '850px',
      data: {xvals: this.xvals, yvals: this.vals, inputs: this.inputs , sensResultData: this.sensResultData , parameters : this.parameters , currencyUnitsLoaded: this.currencyUnitsLoaded , inputTranslations : this.inputTranslations , resultTypes: this.resultTypes , currencyUnits : this.currencyUnits , actualCurrency: this.unitService.getUnitById(baseCurrency)}
    });

    }

  /**
   * Populates the result set dropdown data.
   * @private
   */
  private populateResultSets() {
    if (this.sensResultData) {
      const inputs = Object.keys(this.sensResultData);
      if (inputs && inputs.length > 0) {
        this.resultTypes = Object.keys(this.sensResultData[inputs[0]]);
      }

      const npv = this.resultTypes.find(type => type === ResultSetNames.NPV);
      if (npv) {
        this.selectedResult = npv;
      }
      console.warn(this.selectedResult)
    }
  }

  /**
   * processes the result data and populates the graph data with the correct data the user selected.
   * @private
   */
  private processGraphData() {
    this.curGraphData = [];
    this.showChart = false;
    const tempGraph: PlotlyTrace[] = [];
    this.yMax = 0;
    this.yMin = 0;
    this.xMax = 0;
    this.xMin = 0;
    const vals = [];
    if (this.sensResultData) {
      if (this.selectedParameters && this.selectedResult) {
        this.setYAxisLabel();
        for (const param of this.selectedParameters) {
          if (!this.sensResultData || !this.sensResultData[param][this.selectedResult]) {
            continue;
          }
          if (!Object.keys(this.sensResultData[param][this.selectedResult]).includes('x') ||
            !Object.keys(this.sensResultData[param][this.selectedResult]).includes('y')) {
            // skip all non curve fields in results
            continue;
          }
          const trace: PlotlyTrace = {
            name: this.inputTranslations[param],
            type: 'scatter',
            x: this.sensResultData[param][this.selectedResult].x,
            y: this.valuesInCorrectUnit(this.sensResultData[param][this.selectedResult].y)
          };
          tempGraph.push(trace);
          // @ts-ignore TODO:
          this.xvals.push(...trace.x);
          vals.push(...trace.y);
          this.xMax = Math.max(...this.xvals);
          this.xMin = Math.min(...this.xvals);
          this.yMax = Math.max(...vals);
          this.yMin = Math.min(...vals);
        }

        this.setLayout();
      }
      this.checkForWarnings();
      setTimeout(() => {
        this.curGraphData = [...tempGraph];
        if (this.curGraphData.length > 0) {
          this.showChart = true;
        } else {
          this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.NO_RESULTS_AVAILABLE'));
        }
      });
    } else {
      this.toastrService.warning(this.translationService.get('SENSITIVITY.ERRORS.NO_RESULTS_AVAILABLE'));
    }
  }

  setLayout() {
    const range_max = this.yMax < 0 ? this.yMax / 1.005 : this.yMax * 1.03;
    const range_min = this.yMin < 0 ? this.yMin / 0.995 : this.yMin * 0.95;

    this.graphLayout  = {
      autosize: true,
      width: 1000,
      height: 600,
      xaxis: { title: '' },
      yaxis: {
        range: [range_min, range_max]
      },
      title: '',
      legend: { 'orientation': 'h', x: -0.05, y: -0.3 },
      margin: { l: 50, r: 0, b: 70, t: 50 }
    };
  }

  /**
   * Set the text for the y axis label depending on what data is being shown in the graph.
   * @private
   */
  private setYAxisLabel() {
    if (!this.selectedResult) return;
    const resultTranslation = Utils.processStringForTranslation(this.selectedResult, this.translationService);
    let foundUnit: Unit | null = null;

    switch (this.selectedResult) {
      case ResultSetNames.RNG:
      case ResultSetNames.BIOGAS:
        if (this.selectedUnitsType === UnitLocaleEnum.METRIC) {
          foundUnit = this.unitService.getUnitByName('Nm<sup>3</sup>/year');
        } else {
          foundUnit = this.unitService.getUnitByName('ft<sup>3</sup>/year');
        }
        break;
      case ResultSetNames.NPV:
        foundUnit = this.unitService.getUnitById(this.selectedCurrencyUnit.id);
        break;
      case ResultSetNames.IRR:
        foundUnit = this.unitService.getUnitByName('%');
        break;
      case ResultSetNames.PBP:
        foundUnit = this.unitService.getUnitByName('years');
        break;
      case ResultSetNames.ELEC:
        foundUnit = this.unitService.getUnitByName('kWh/year');
        break;
    }
    if (!foundUnit) throw Error('failed to find unit for graph axis');
    const unitDisplayName = this.translationService.get(foundUnit.translationId);
    const processedUnitName = Unit.parseUnitDisplayName(unitDisplayName);
    this.yAxisLabel = resultTranslation + ' (' + processedUnitName + ')';
  }

  /**
   * Convert the provided array of values to the correct unit.
   * @param y an array of numbers that will be converted.
   * @private
   */
  private valuesInCorrectUnit(y: number[]): number[] {
    if (this.selectedUnitsType === UnitLocaleEnum.IMPERIAL &&
      (this.selectedResult === ResultSetNames.BIOGAS || this.selectedResult === ResultSetNames.RNG)) {
      const imperialUnit = this.unitService.getUnitById('000000000000000000003813');
      const metricUnit = this.unitService.getUnitById('000000000000000000003812');
      const convertedValues: number[] = [];
      for (const value of y) {
        convertedValues.push(metricUnit.convertToUnit(Utils.deepCopy(value), imperialUnit));
      }
      return convertedValues;
    }
    if (this.selectedResult === ResultSetNames.NPV && this.selectedCurrencyUnit) {
      let baseCurrency = this.unitService.getUnitById(this.resultsBaseCurrencyId);
      if (!baseCurrency) {
        baseCurrency = this.unitService.getUnitById(this.projectCommService.getBaseCurrencyId());
      }
      const convertedValues: number[] = [];
      for (const value of y) {
        convertedValues.push(baseCurrency.convertToUnit(Utils.deepCopy(value), this.selectedCurrencyUnit));
      }
      return convertedValues;
    }
    return y;
  }


  /**
   * Populate the currency units data array.
   * @private
   */
  private populateCurrencyUnits(exchangeRateDate: string) {
    if (this.unitServiceReady && this.projectServiceReady && this.resultsLoaded) {
      this.unitService.getCurrencyUnits(exchangeRateDate).then(
        (units: Unit[]) => {
          this.exchangeRateDate = this.unitService.getExchangeRateDate();
          this.currencyUnits = units;

          // sort rates alphabetically
          this.currencyUnits.sort((first: Unit, second: Unit) => {
            if (Number(first.order) < Number(second.order)) {
              return -1;
            } else if (Number(second.order) < Number(first.order)) {
              return 1;
            } else {
              return 0;
            }
          });
          // Select default unit if set or CAD otherwise
          if (this.projectCommService.getProjectDefaults().currency != null) {
            this.selectedCurrencyUnit = this.unitService.getUnitById(this.projectCommService.getProjectDefaults().currency);
          } else {
            this.selectedCurrencyUnit = this.currencyUnits.find(currency => currency.displayName === 'CAD');
          }
          this.currencyUnitsLoaded = true;
          this.optionsChanged();
        }
      );
    }
  }

  /**
   * Check for warnings for the current result set and show them to the user to explain why there might not
   * be an data.
   * @private
   */
  private checkForWarnings() {
    if (!this.selectedResult) return;

    const warnings: string[] = [];
    for (const param of this.selectedParameters) {
      if (!this.sensResultData[param][this.selectedResult].x ||
        this.sensResultData[param][this.selectedResult].x.length === 0 ||
        !this.sensResultData[param][this.selectedResult].y ||
        this.sensResultData[param][this.selectedResult].y.length === 0
      ) {
        warnings.push(param);
      }
    }
    if (warnings && warnings.length > 0) {
      const resultTranslation = this.translationService.get(this.selectedResult);
      let message = this.translationService.get('SENSITIVITY.RESULTS.NOT_FEASIBLE', { result: resultTranslation });
      // let message = 'the following ' + this.selectedResult + ' results were not feasible: ';
      for (let i = 0; i < warnings.length; i++) {
        message += warnings[i];
        if (i !== warnings.length - 1) {
          message += '|';
        }
      }
      this.toastrService.warning(message, undefined, 5);
    }
  }


  /**
   * Changes status of all options to selected or unselected
   * @param $event click event
   */
  inputsCheckAll($event: MatCheckboxChange): void {
    if ($event.checked) {
      this.selectedParameters = [...this.parameters];
    } else {
      this.selectedParameters = [];
    }
    this.optionsChanged();
  }

  /**
   * Checks if all options are selected
   */
  allInputsSelected(): boolean {
    for (const item of this.parameters) {
      if (!this.selectedParameters.includes(item)) {
        return false;
      }
    }
    return true;
  }

  onSave(){
    this.showPopUp();
  }

  /**
   * Checks if at least one option is selected
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    for (const item of this.parameters) {
      if (!this.selectedParameters.includes(item)) {
        atLeastOneIncluded = true;
      } else {
        allIncluded = false;
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  private processInputTranslations() {
    this.inputTranslations = {};
    if (!this.inputs) return;
    for (const input of this.inputs) {
      this.inputTranslations[input.id] = ResultsUtils.processNameElements(this.translationService, input.nameElements);
    }
  }
}

enum ResultSetNames {
  NPV = 'RESULTS.DATA.ECONOMIC_METRICS.NPV',
  IRR = 'RESULTS.DATA.ECONOMIC_METRICS.IRR',
  PBP = 'RESULTS.DATA.ECONOMIC_METRICS.PP',
  BIOGAS = 'PRODUCTS.BIOGAS',
  RNG = 'PRODUCTS.BIOMETHANE',
  ELEC = 'PRODUCTS.ELECTRICITY'
}
